import { usePostHog } from "@posthog/react";
import type { PostHogConfig } from "posthog-js";
import { useCallback } from "react";

/**
 * Product analytics for bibleshield.app.
 *
 * The site exists to hand people to the app: a shared `/post/…` or `/request/…`
 * link opens the app directly when it is installed, so ANYONE who reaches this
 * website through one is by definition someone who does not have it. That makes
 * these pages the top of the install funnel and, until now, the only part of it
 * with no measurement at all — the app records the share going out
 * (`community_share_opened`) and the arrival coming back
 * (`community_share_link_opened`), and the web step in between was invisible.
 *
 * The same three restraints the mobile client works under (see
 * apps/mobile/src/lib/analytics.ts), for the same reasons:
 *
 *  1. NO USER CONTENT, and here that is stricter than a preference. The share
 *     routes carry an opaque code in the path, and `SharedItem` deliberately
 *     renders nothing about the record. Sending that code to PostHog would put
 *     a per-item identifier in an analytics store that the page itself refuses
 *     to resolve — so events carry the KIND of thing shared, never which one.
 *  2. NO PERSONAL DATA. Nobody is signed in here; there is no device id to
 *     identify against and none is invented.
 *  3. FEW, DELIBERATE EVENTS. PostHog bills per event and is already this
 *     product's largest infrastructure line at scale.
 */

/**
 * The PostHog project token — public by design (it is `phc_`-prefixed, ships in
 * every website's JavaScript, and can only write events).
 *
 * Committed as a default rather than left to the environment alone, which is
 * the opposite of how the mobile client does it, and deliberately: mobile reads
 * its key from EAS, which is a managed environment that is always present at
 * build time. This site is built and deployed by hand (`pnpm build && wrangler
 * deploy`), with no environment pipeline behind it — so an env-only key means
 * one deploy from a machine without a `.env` silently ships a site that
 * measures nothing, with no error and nothing to notice. `VITE_…` still wins
 * when set, so pointing a build at a different project needs no code change.
 */
const DEFAULT_TOKEN = "phc_umhhqXk38jNYiojXG7zgXVGsASEdDqiWNcAFJLRvv3Ut";
const DEFAULT_HOST = "https://us.i.posthog.com";

export const POSTHOG_TOKEN =
  import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || DEFAULT_TOKEN;
export const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || DEFAULT_HOST;

/**
 * Analytics runs in production builds only.
 *
 * `vite dev` on localhost would otherwise file every save and every hot reload
 * as real traffic into the same project the install funnel is read from — and
 * on a site this small, a single afternoon of local work would be most of the
 * data.
 */
export const analyticsEnabled =
  import.meta.env.PROD && POSTHOG_TOKEN.length > 0;

export const posthogOptions: Partial<PostHogConfig> = {
  api_host: POSTHOG_HOST,
  // Pins the SDK's recommended defaults to a date, so a future posthog-js
  // release cannot change what is collected without an explicit bump here.
  // Among them is history-API pageview capture, which is what makes client-side
  // navigation between /privacy and /terms count as two views rather than one.
  defaults: "2026-05-30",
  // Nobody signs in on this site, so there is no identity to attach and no
  // reason to keep a cross-session profile of an anonymous reader.
  person_profiles: "never",

  /**
   * Surveys OFF, and this is a performance fix rather than a preference.
   *
   * PostHog loads `surveys.js` on every page whether or not a survey exists —
   * Lighthouse measured 34 KiB transferred, 27 KiB of it never executed, plus
   * 8ms of main thread. It was the single largest third-party cost on the site
   * and it was buying nothing: this project has no surveys and the remote
   * config already reports `surveys: false`.
   */
  disable_surveys: true,

  /**
   * Autocapture OFF.
   *
   * It was on by default and it contradicted the rule at the top of this file:
   * few, deliberate events. A single visit was filing a pageview, a pageleave,
   * an autocapture per click and the occasional rageclick — five or six rows to
   * answer a question ("how many people came, and did they tap the store
   * button") that two of them already answer.
   *
   * What is kept is exactly what that question needs: `$pageview` for visits,
   * `$pageleave` for whether they stayed, and the handful of events this site
   * declares by name. Store taps are instrumented explicitly (`web_store_click`
   * with the section that produced them), which is better data than autocapture
   * would have inferred from an anchor's text.
   *
   * The cost saved is real but small at this traffic. The clarity is the point:
   * every event in the project is now one somebody chose to send.
   */
  autocapture: false,
};

/** Events this site sends. A closed list, so adding one is a decision. */
type WebEvent =
  /** A shared link opened on the WEB — i.e. by someone without the app. Carries
   *  the `utm_source` surface that produced the link, so this arrival joins the
   *  app's `community_share_opened` at the other end (GEN-12). */
  | "web_share_landing_viewed"
  /** A store button tapped: the actual handoff this site exists for. */
  | "web_store_click"
  /** The `bibleshield://` deep link on /delete, which only works if installed. */
  | "web_app_open_click"
  /** A quiz answered to the last question, with the outcome it landed on.
   *  Separate from a pageview on purpose: an article is read or not, but a quiz
   *  is started far more often than it is finished, and those are different
   *  problems. The outcome distribution is also the only way to find out that a
   *  result nobody ever gets is written badly rather than rare. */
  | "web_quiz_completed"
  /** The share button on a result. The one measurable thing on this site that
   *  produces a link from a person rather than from us. */
  | "web_quiz_share"
  /** Core Web Vitals from a real visit, once per page view — see lib/vitals.ts.
   *  Field data rather than a lab score: Google ranks on what devices actually
   *  measured, and a synthetic run from one machine cannot tell you that. */
  | "web_vitals";

/** Event properties. Same shape rule as the mobile client: no free-form
 *  strings wide enough to carry content. */
type WebEventProperties = Record<string, string | number | boolean | null>;

/**
 * Which AI assistant sent this visitor, if one did.
 *
 * ── Why this is worth a module ───────────────────────────────────────────────
 *
 * The whole GEO effort — llms.txt, the takeaways block, the structured data —
 * is a bet that assistants will start citing these pages. Without this, the bet
 * has no scoreboard: those arrivals land in PostHog as an ordinary direct or
 * referral visit and are indistinguishable from everything else.
 *
 * Registered as a SUPER PROPERTY rather than captured as its own event, so it
 * rides on every subsequent event in the session. That is what makes the useful
 * question answerable — not "how many came from Perplexity" but "do the ones
 * who came from Perplexity tap the store button more or less than the ones who
 * came from Google".
 *
 * Referrer only. No fingerprinting, nothing stored beyond what the browser
 * already sends, and it is absent for the assistants that strip the referrer —
 * which is a known undercount and better than a guess dressed as a number.
 */
const AI_REFERRERS: [RegExp, string][] = [
  [/(^|\.)chatgpt\.com$/, "chatgpt"],
  [/(^|\.)openai\.com$/, "chatgpt"],
  [/(^|\.)perplexity\.ai$/, "perplexity"],
  [/(^|\.)claude\.ai$/, "claude"],
  [/(^|\.)anthropic\.com$/, "claude"],
  [/(^|\.)copilot\.microsoft\.com$/, "copilot"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)gemini\.google\.com$/, "gemini"],
];

/** The assistant behind a referrer URL, or null. Exported for its test. */
export function aiReferrerOf(referrer: string): string | null {
  if (!referrer) return null;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return null;
  }
  for (const [pattern, name] of AI_REFERRERS) {
    if (pattern.test(host)) return name;
  }
  return null;
}

/**
 * `track` bound to this page's PostHog client.
 *
 * A hook rather than a module-level function because the client lives in React
 * context — which is also what makes it correct under SSR: the provider only
 * calls `init` from an effect, so on the Worker there is nothing to send to and
 * nothing tries.
 *
 * MEMOIZED, and that is not a micro-optimisation. `SharedItem` fires its landing
 * event from a `useEffect` that depends on this function; an unstable identity
 * would re-run that effect on every single render and file one
 * `web_share_landing_viewed` per render instead of one per visit — inflating the
 * top of the install funnel by an unknown multiple and billing for every copy.
 */
export function useTrack(): (
  event: WebEvent,
  properties?: WebEventProperties,
) => void {
  const posthog = usePostHog();
  return useCallback(
    (event: WebEvent, properties?: WebEventProperties) => {
      if (!analyticsEnabled) return;
      posthog?.capture(event, properties);
    },
    [posthog],
  );
}

/** Which store a button points at — kept as a union so a typo can't quietly
 *  create a third store in the funnel. */
export type Store = "appstore" | "play";
